/** Sentinel id for the (non-closeable) default tab within the unified tab order. */
export const DEFAULT_TAB_ID = '__default__';

/**
 * Build the unified ordered list of tab ids — the pinned report codes with the
 * default tab's slot inserted at `defaultIndex` (clamped into range). This is
 * the single source of truth the drag-reorder and the left/right close actions
 * read positions from.
 */
export const buildTabIds = (pinnedCodes: string[], defaultIndex: number): string[] => {
  const idx = Math.min(Math.max(defaultIndex, 0), pinnedCodes.length);
  const ids = [...pinnedCodes];
  ids.splice(idx, 0, DEFAULT_TAB_ID);
  return ids;
};

/** Inverse of {@link buildTabIds}: recover the report codes and the default slot's index. */
export const splitTabIds = (tabIds: string[]): { pinnedCodes: string[]; defaultIndex: number } => ({
  pinnedCodes: tabIds.filter((id) => id !== DEFAULT_TAB_ID),
  defaultIndex: Math.max(0, tabIds.indexOf(DEFAULT_TAB_ID)),
});

/**
 * Report codes that a close-action would remove relative to `targetId`:
 * `left`/`right` = closeable report tabs positioned that side of the target;
 * `others` = every closeable report tab except the target. The default tab is
 * never closeable, so it is always excluded.
 */
export const closeableCodes = (
  tabIds: string[],
  targetId: string,
  side: 'left' | 'right' | 'others'
): string[] => {
  const targetIdx = tabIds.indexOf(targetId);
  return tabIds.filter((id, i) => {
    if (id === DEFAULT_TAB_ID || id === targetId) return false;
    if (side === 'left') return i < targetIdx;
    if (side === 'right') return i > targetIdx;
    return true;
  });
};
